import { ColumnInfo, ColumnInfoList, DisplaySetting } from 'components/shared/display_setting'
import { TableCell } from 'components/shared/widget'
import { PcrProcessLog } from 'entities/pcr_process_log'
import { ReactNode, useMemo } from 'react'
import { Badge } from 'react-bootstrap'
import { Form } from 'utils/hooks'
import { AiOutlineCheckCircle, AiOutlineCloseCircle } from 'react-icons/ai'
import { QcTypeBadge } from 'view_models/result_view_model'

export const pcrProcessLogColumnInfoList = (form?: Form<Record<string, unknown>>): ColumnInfoList => {
    return {
        tableName: 'Process Log',
        values: [
            {
                name: '結果',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_result',
                searchType: {
                    name: 'result',
                    type: 'multiple_select',
                    selectItems: [
                        { label: 'OK', value: 'true' },
                        { label: 'NG', value: 'false' },
                    ],
                },
                align: 'center',
            },
            {
                name: 'cDNA ID',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_dna_code',
                searchType: { name: 'dna_code', type: 'multiple_text' },
                align: 'center',
            },
            {
                align: 'center',
                name: 'プローブ',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_probe',
                searchType: { name: 'probe', type: 'multiple_text' },
            },
            {
                align: 'center',
                name: 'プローブロット',
                th: true,
                width: 150,
            },
            {
                align: 'center',
                name: '製品',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_product',
                searchType: { name: 'product', type: 'multiple_text' },
            },
            {
                align: 'center',
                name: 'QC Type',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_qc_type',
                searchType: {
                    name: 'qc_type',
                    type: 'multiple_select',
                    selectItems: ['RNA_SPIKE_IN', 'RT_SPIKE_IN', 'HK'].map((qcType) => ({ label: qcType, value: qcType })),
                },
            },
            { align: 'center', name: 'CT', th: true, width: 120, form: form, orderAttr: 'pcr_process_log_ct', searchType: { name: 'ct', type: 'number' } },
            {
                align: 'center',
                name: 'Norm CT',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_norm_ct',
                searchType: { name: 'norm_ct', type: 'number' },
            },
            {
                align: 'center',
                name: '最小値',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_min',
                searchType: { name: 'min', type: 'number' },
            },
            {
                align: 'center',
                name: '最大値',
                th: true,
                width: 120,
                form: form,
                orderAttr: 'pcr_process_log_max',
                searchType: { name: 'max', type: 'number' },
            },
            {
                name: 'アクション',
                th: true,
                align: 'center',
                width: 200,
                form: form,
                orderAttr: 'pcr_process_log_action',
                searchType: { name: 'action', type: 'multiple_text' },
            },
        ],
    }
}

export type PcrProcessLogTableDataProps = {
    pcrProcessLog?: PcrProcessLog
    displaySetting: DisplaySetting
}

export const PcrProcessLogTableData = ({ pcrProcessLog, displaySetting }: PcrProcessLogTableDataProps): JSX.Element => {
    if (!pcrProcessLog) {
        return (
            <>
                {displaySetting.columnInfoList.values.map((_: ColumnInfo, index: number) => {
                    if (displaySetting.hiddenList.includes(index)) {
                        return <></>
                    } else {
                        return <TableCell key={index}></TableCell>
                    }
                })}
            </>
        )
    }

    const createCells = useMemo(() => {
        return (
            <>
                <TableCell align="center">{<Badge bg={pcrProcessLog.result ? 'success' : 'danger'}>{pcrProcessLog.result ? 'OK' : 'NG'}</Badge>}</TableCell>
                <TableCell align="center">
                    {pcrProcessLog.dnaCode === 'Blank' && <AiOutlineCloseCircle size={18} style={{ color: 'red' }} />}
                    {pcrProcessLog.dnaCode === 'qPCR_standard' && <AiOutlineCheckCircle size={18} style={{ color: 'blue' }} />}
                    {pcrProcessLog.dnaCode}
                </TableCell>
                <TableCell align="center">{pcrProcessLog.probe}</TableCell>
                <TableCell align="center">{pcrProcessLog.probeStock?.lotNumber}</TableCell>
                <TableCell align="center">{pcrProcessLog.product}</TableCell>
                <TableCell align="center">{<QcTypeBadge qcType={pcrProcessLog.qcType} />}</TableCell>
                <TableCell align="center">{pcrProcessLog.ct?.toFixed(2)}</TableCell>
                <TableCell align="center">{pcrProcessLog.normalizedCt?.toFixed(2)}</TableCell>
                <TableCell align="center">{pcrProcessLog.min?.toFixed(2)}</TableCell>
                <TableCell align="center">{pcrProcessLog.max?.toFixed(2)}</TableCell>
                <TableCell align="center">{pcrProcessLog.action ?? 'なし'}</TableCell>
            </>
        )
    }, [displaySetting.hiddenList])

    return createCells.props.children.filter((_: ReactNode, index: number) => !displaySetting.hiddenList?.includes(index)).map((cell: ReactNode) => cell)
}
